/* global NodeJS */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Mail, CheckCircle2, AlertCircle, Phone, Copy, Printer, Share2, FileText, MapPin } from 'lucide-react';
import { useLanguageStore } from '../store/languageStore';
import { useMatchStore } from '../store/matchStore';
import { generateAppealLetter } from '../api/gpt';
import { motion } from 'framer-motion';

interface LocationState {
  resource: {
    name: string;
    type: string;
    contact: {
      phone: string;
      email: string;
      website: string;
    };
    address: {
      street: string;
      city: string;
      state: string;
      zip: string;
    };
  };
}

const messages = {
  EN: {
    back: "Back to Details",
    title: "Appeal Letter",
    subtitle: "We'll help you create a compelling appeal letter for your application",
    contactInfo: "Contact Information",
    importantNote: "Important Note",
    replaceText: "Please review and customize the letter with your specific details before sending.",
    letterSent: "Letter has been sent successfully!",
    copyLetter: "Copy Letter",
    downloadLetter: "Download Letter",
    printLetter: "Print Letter",
    shareLetter: "Share Letter",
    customizeLetter: "Customize Letter",
    previewLetter: "Preview Letter",
    sendLetter: "Send Letter",
    error: "Error generating letter. Please try again.",
    appealScript: "Appeal Letter Script",
    appealScriptDesc: "Use this template to write your appeal letter",
    address: "Address",
    phone: "Phone",
    email: "Email",
    website: "Website",
    close: "Close"
  },
  ES: {
    back: "Volver a Detalles",
    title: "Carta de Apelación",
    subtitle: "Te ayudaremos a crear una carta de apelación convincente para tu solicitud",
    contactInfo: "Información de Contacto",
    importantNote: "Nota Importante",
    replaceText: "Por favor revise y personalice la carta con sus detalles específicos antes de enviarla.",
    letterSent: "¡La carta ha sido enviada exitosamente!",
    copyLetter: "Copiar Carta",
    downloadLetter: "Descargar Carta",
    printLetter: "Imprimir Carta",
    shareLetter: "Compartir Carta",
    customizeLetter: "Personalizar Carta",
    previewLetter: "Vista Previa",
    sendLetter: "Enviar Carta",
    error: "Error generando carta. Por favor intente de nuevo.",
    appealScript: "Guión de Carta de Apelación",
    appealScriptDesc: "Use esta plantilla para escribir su carta de apelación",
    address: "Dirección",
    phone: "Teléfono",
    email: "Correo Electrónico",
    website: "Sitio Web",
    close: "Cerrar"
  },
  ZH: {
    back: "返回详情",
    title: "上诉信",
    subtitle: "我们将帮助您为申请创建一封有说服力的上诉信",
    contactInfo: "联系信息",
    importantNote: "重要提示",
    replaceText: "请在发送前查看并自定义信件中的具体细节。",
    letterSent: "信件已成功发送！",
    copyLetter: "复制信件",
    downloadLetter: "下载信件",
    printLetter: "打印信件",
    shareLetter: "分享信件",
    customizeLetter: "自定义信件",
    previewLetter: "预览信件",
    sendLetter: "发送信件",
    error: "生成信件时出错。请重试。",
    appealScript: "上诉信模板",
    appealScriptDesc: "使用此模板撰写您的上诉信",
    address: "地址",
    phone: "电话",
    email: "电子邮件",
    website: "网站",
    close: "关闭"
  },
  VI: {
    back: "Quay lại Chi Tiết",
    title: "Thư Kháng Cáo",
    subtitle: "Chúng tôi sẽ giúp bạn tạo một lá thư kháng cáo thuyết phục cho đơn đăng ký",
    contactInfo: "Thông Tin Liên Hệ",
    importantNote: "Lưu Ý Quan Trọng",
    replaceText: "Vui lòng xem xét và tùy chỉnh thư với chi tiết cụ thể của bạn trước khi gửi.",
    letterSent: "Thư đã được gửi thành công!",
    copyLetter: "Sao Chép Thư",
    downloadLetter: "Tải Xuống Thư",
    printLetter: "In Thư",
    shareLetter: "Chia Sẻ Thư",
    customizeLetter: "Tùy Chỉnh Thư",
    previewLetter: "Xem Trước",
    sendLetter: "Gửi Thư",
    error: "Lỗi tạo thư. Vui lòng thử lại.",
    appealScript: "Mẫu Thư Kháng Cáo",
    appealScriptDesc: "Sử dụng mẫu này để viết thư kháng cáo của bạn",
    address: "Địa Chỉ",
    phone: "Điện Thoại",
    email: "Email",
    website: "Trang Web",
    close: "Đóng"
  },
  FIL: {
    back: "Bumalik sa Detalye",
    title: "Liham ng Apela",
    subtitle: "Tutulungan ka naming gumawa ng nakakumbinsing liham ng apela para sa iyong aplikasyon",
    contactInfo: "Impormasyon ng Kontak",
    importantNote: "Mahalagang Paalala",
    replaceText: "Mangyaring suriin at i-customize ang liham kasama ang iyong mga tiyak na detalye bago ipadala.",
    letterSent: "Matagumpay na naipadala ang liham!",
    copyLetter: "Kopyahin ang Liham",
    downloadLetter: "I-download ang Liham",
    printLetter: "I-print ang Liham",
    shareLetter: "Ibahagi ang Liham",
    customizeLetter: "I-customize ang Liham",
    previewLetter: "I-preview ang Liham",
    sendLetter: "Ipadala ang Liham",
    error: "Error sa paggawa ng liham. Mangyaring subukan muli.",
    appealScript: "Template ng Liham ng Apela",
    appealScriptDesc: "Gamitin ang template na ito para sumulat ng iyong liham ng apela",
    address: "Address",
    phone: "Telepono",
    email: "Email",
    website: "Website",
    close: "Isara"
  },
  KO: {
    back: "상세로 돌아가기",
    title: "항의 편지",
    subtitle: "신청서를 위한 설득력 있는 항의 편지를 작성하는 데 도움을 드리겠습니다",
    contactInfo: "연락처 정보",
    importantNote: "중요한 참고사항",
    replaceText: "보내기 전에 편지를 검토하고 구체적인 세부사항으로 맞춤 설정하세요.",
    letterSent: "편지가 성공적으로 전송되었습니다!",
    copyLetter: "편지 복사",
    downloadLetter: "편지 다운로드",
    printLetter: "편지 인쇄",
    shareLetter: "편지 공유",
    customizeLetter: "편지 맞춤 설정",
    previewLetter: "편지 미리보기",
    sendLetter: "편지 보내기",
    error: "편지 생성 중 오류가 발생했습니다. 다시 시도해 주세요.",
    appealScript: "항의 편지 템플릿",
    appealScriptDesc: "이 템플릿을 사용하여 항의 편지를 작성하세요",
    address: "주소",
    phone: "전화",
    email: "이메일",
    website: "웹사이트",
    close: "닫기"
  },
  AR: {
    back: "العودة إلى التفاصيل",
    title: "خطاب الاستئناف",
    subtitle: "سن帮助你创建一封有说服力的上诉信",
    contactInfo: "معلومات الاتصال",
    importantNote: "ملاحظة مهمة",
    replaceText: "يرجى مراجعة وتخصيص الخطاب مع تفاصيلك المحددة قبل الإرسال.",
    letterSent: "تم إرسال الخطاب بنجاح!",
    copyLetter: "نسخ الخطاب",
    downloadLetter: "تحميل الخطاب",
    printLetter: "طباعة الخطاب",
    shareLetter: "مشاركة الخطاب",
    customizeLetter: "تخصيص الخطاب",
    previewLetter: "معاينة الخطاب",
    sendLetter: "إرسال الخطاب",
    error: "خطأ في إنشاء الخطاب. يرجى المحاولة مرة أخرى.",
    appealScript: "قالب خطاب الاستئناف",
    appealScriptDesc: "استخدم هذا القالب لكتابة خطاب الاستئناف الخاص بك",
    address: "العنوان",
    phone: "الهاتف",
    email: "البريد الإلكتروني",
    website: "الموقع الإلكتروني",
    close: "إغلاق"
  }
};

const AppealLetter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedLanguage } = useLanguageStore();
  const { matchResults } = useMatchStore();
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [letter, setLetter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  
  const { resource: stateResource } = (location.state as LocationState) || {};
  const resource = stateResource;

  // Fixed useEffect for appeal letter generation with proper async handling
  useEffect(() => {
    if (resource) {
      generateAppealLetterScript();
    }
  }, [resource, selectedLanguage]);

  const generateAppealLetterScript = async () => {
    if (!resource) return;
    
    setIsGenerating(true);
    setError(null);
    
    try {
      const appealScript = await generateAppealLetter(resource.name, resource.type, selectedLanguage);
      setLetter(appealScript);
    } catch (err) {
      setError(messages[selectedLanguage].error);
      console.error('Error generating appeal letter:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(letter);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([letter], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `appeal-letter-${resource?.name || 'program'}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    setShowCompletionMessage(true);
    setTimeout(() => setShowCompletionMessage(false), 2000);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Appeal Letter</title>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; padding: 2rem; }
              .letter { max-width: 800px; margin: 0 auto; }
            </style>
          </head>
          <body>
            <div class="letter">
              ${letter.replace(/\n/g, '<br>')}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: 'Appeal Letter',
        text: letter,
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  if (!resource) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {messages[selectedLanguage].error}
            </h1>
            <button
              onClick={() => navigate("/details")}
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {messages[selectedLanguage].back}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mt-64 sm:mt-48">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {messages[selectedLanguage].back}
          </button>
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-8"
        >
          <motion.div variants={itemVariants}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
              <div className="p-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {messages[selectedLanguage].title}
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                  {messages[selectedLanguage].subtitle}
                </p>

                {/* Contact Information */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6">
                  <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-3">
                    {messages[selectedLanguage].contactInfo}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                      <Phone className="w-4 h-4" />
                      <span>{resource.contact.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                      <Mail className="w-4 h-4" />
                      <span>{resource.contact.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                      <MapPin className="w-4 h-4" />
                      <span>{resource.address.street}, {resource.address.city}, {resource.address.state} {resource.address.zip}</span>
                    </div>
                  </div>
                </div>

                {/* Important Note */}
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-yellow-900 dark:text-yellow-100 mb-1">
                        {messages[selectedLanguage].importantNote}
                      </h3>
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        {messages[selectedLanguage].replaceText}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Appeal Letter Script */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 mb-6">
                  <h3 className="font-medium text-green-700 dark:text-green-300 mb-3 flex items-center gap-2">
                    📝 {messages[selectedLanguage].appealScript}
                  </h3>
                  <p className="text-sm text-green-600 dark:text-green-400 mb-4">
                    {messages[selectedLanguage].appealScriptDesc}
                  </p>

                {isGenerating ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  </div>
                ) : (
                    <div className="bg-green-100 dark:bg-green-900/40 rounded-lg p-6 font-mono text-sm text-green-800 dark:text-green-200 whitespace-pre-wrap mb-4">
                      {letter || 'Loading appeal letter...'}
                    </div>
                  )}

                  {/* Action Buttons - Moved here to be near the generated letter */}
                  {letter && !isGenerating && (
                    <div className="flex flex-wrap gap-3 mt-4">
                      <button
                        onClick={handleCopy}
                        className="flex items-center justify-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 text-sm"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        {isCopied ? 'Copied!' : messages[selectedLanguage].copyLetter}
                      </button>
                      <button
                        onClick={handleDownload}
                        className="flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 text-sm"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {messages[selectedLanguage].downloadLetter}
                      </button>
                      <button
                        onClick={handlePrint}
                        className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 text-sm"
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        {messages[selectedLanguage].printLetter}
                      </button>
                      <button
                        onClick={handleShare}
                        className="flex items-center justify-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 text-sm"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        {messages[selectedLanguage].shareLetter}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Success Modal */}
                {showCompletionMessage && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md mx-4">
                      <div className="flex items-center gap-3 text-green-600 dark:text-green-400 mb-4">
                        <CheckCircle2 className="w-6 h-6" />
                        <h3 className="text-lg font-semibold">
                {messages[selectedLanguage].letterSent}
                        </h3>
                      </div>
                      <button
                        onClick={() => setShowCompletionMessage(false)}
                        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                      >
              {messages[selectedLanguage].close}
                      </button>
                    </div>
                  </div>
                )}
    </div>
  );
};

export default AppealLetter;